import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LoginIcon from "@mui/icons-material/Login";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import SyncIcon from "@mui/icons-material/Sync";
import { useCallback, useEffect, useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import DangerConfirmDialog from "../components/DangerConfirmDialog";
import { friendlyError } from "../utils/apiError";
import { tokens } from "../theme";
import {
  CONTENT_OWNER_KIND_OPTIONS,
  foyerApi,
  type ContentOwnerKindValue,
  type ContentOwnerRecord,
  type ContentSupplierRow,
} from "../http/foyer";
import type { SignagePlaylist } from "@shared/types/signage";

/**
 * A live preview only — a mirror of the server's own slugify
 * (`mintProviderId` in `contentPackages.ts`, `contentOwnerSlug` in
 * `@shared/content-owner`), not a shared import of either.
 *
 * `@shared/content-owner` is not in `foyer-shared/package.json`'s `exports`
 * map, so a runtime import of it here would typecheck fine under the
 * monorepo lane and then fail to resolve in the standalone build that
 * actually ships (see `global/pitfalls/shared-alias-build-lanes.md`) — a
 * type-only import is erased and safe, a value import is not. The server is
 * still the actual source of truth and derives its own copy independently;
 * this only has to look right while typing.
 */
function slugPreview(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * A client-side mirror of `isMrssFeedUrl` (`@shared/content-owner`), same
 * caveat as `slugPreview` above: a value import of that module is not safe in
 * the standalone build that actually ships. The server stays the one
 * authoritative check.
 */
function looksLikeMrssFeedUrl(value: string): boolean {
  if (value.trim() === "") return true; // empty is valid - it means "no feed"
  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Content providers — who Nenda has licensed, and who is TRUSTED.
 *
 * ## Why this is not a tab on Packages
 *
 * It used to sit on that page, above the packages themselves, and it made the
 * screen read as though a provider were a detail of a package. It is not. Four
 * separate subsystems read `ContentSupplier`:
 *
 *   - `entitlementResolver` — the play gate. Only a TRUSTED provider's content
 *     is carried by a rule, so this screen decides what a guest can watch.
 *   - `bulkIngestScan` — an upload is attributed to the provider the account is
 *     bound to, never to the one the metadata claims.
 *   - `ContentOwner` / `AssetOwner` — a provider's own login to the
 *     content-owner portal (its id IS this row's id — see below).
 *   - `contentPackages` — rules.
 *
 * Nesting it under Packages would encode a relationship that does not exist,
 * and packages are the LAST of the four to matter.
 *
 * ## Login is a capability of a provider, not a second organisation, and not
 * a second SCREEN either
 *
 * Ruled by Sigge 2026-09-04 (`[[mrss-feed-content-providers]]`): a content
 * provider can be catalogue-attribution-only (SVT, TV4 Media — no login,
 * ever), or it can also have a login to upload and read its own numbers
 * (Tigra). Either way it is ONE row, at ONE id — the real upstream
 * `provider.providerId`.
 *
 * The first cut of this ruling still rendered a whole second card below this
 * table listing every login as its own organisation — the same "two lists"
 * shape the ruling was supposed to remove, just moved down a level. Corrected
 * the same day: the **Login** column is a pure STATUS marker — an edit icon
 * when a login exists, nothing at all when it does not.
 *
 * A second cut then added a "give login" icon here too — and Sigge reversed
 * that the same day (`[[mrss-feed-content-providers]]`): a login with nobody
 * to use it is not a step forward, so the ONE place that creates one is the
 * Users page, as part of giving a real person access — see `ScopePicker` in
 * `UsersPage.tsx`. This page only ever shows a login that already has
 * somebody attached to it. And "which USER is tied to which provider" is
 * answered on the Users page too, in the Scope column — a question about a
 * login *account*, not about the provider, and belongs where accounts live.
 *
 * ## The MRSS feed is a fact about the PROVIDER, not the login
 *
 * First cut, same day: the feed lived on `ContentOwner`, so attaching one
 * required a login to exist first. Wrong within hours — DroneTV is a
 * feed-only aggregator (AirVuz/DroneTV) with nobody at the source ever
 * logging in, and it still needed a feed. Moved to `ContentSupplier` itself:
 * a login and a feed are two independent capabilities a provider may hold,
 * neither implying the other. Unlike Login, this column both shows AND
 * creates the feed right here — a feed needs nobody to use it, so there is
 * no Users-page-style reason to route its creation elsewhere.
 *
 * ## And the permissions differ, which settles it
 *
 * Seeing this screen is `canManageDistribution`. **Trusting a provider is a
 * second key, `canTrustSuppliers`, enforced on the route.** Half a page cannot
 * sensibly be gated on a different permission from the other half — so a tab
 * was never really available, only a tab-shaped nav item.
 *
 * The split is deliberate: seeing the catalogue is part of managing it;
 * committing Nenda to a supplier is a different act with a different blast
 * radius. A rule over a newly-trusted provider changes what plays in every room
 * that holds a package naming it.
 */
export default function ContentProvidersPage() {
  const [suppliers, setSuppliers] = useState<ContentSupplierRow[]>([]);
  const [catalogueReachable, setCatalogueReachable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sups = await foyerApi.listContentSuppliers();
      setSuppliers(sups.suppliers);
      setCatalogueReachable(sups.catalogueReachable);
      setError(null);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const trustedCount = suppliers.filter((s) => s.trusted).length;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Distribution"
        title="Content providers"
        subtitle="Who Nenda carries in the catalogue, who is trusted, and who has a login."
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {!catalogueReachable ? (
        // Our own answer is still in hand, so the screen works. Saying which
        // half is missing beats a spinner that never resolves.
        <Alert severity="warning" sx={{ mb: 3 }}>
          The content catalogue is unreachable, so this list shows only the
          content providers Foyer already knows about. Trust decisions still
          apply.
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <SuppliersCard
          suppliers={suppliers}
          trustedCount={trustedCount}
          onChanged={load}
        />
      )}
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */

function SuppliersCard({
  suppliers,
  trustedCount,
  onChanged,
}: {
  suppliers: ContentSupplierRow[];
  trustedCount: number;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingLogin, setEditingLogin] = useState<ContentOwnerRecord | null>(
    null
  );
  const [editingFeed, setEditingFeed] = useState<ContentSupplierRow | null>(
    null
  );
  const [deleting, setDeleting] = useState<ContentSupplierRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Only fetched for the ones that have a login, to fill the edit dialog -
  // the "has one at all" fact already travels on the supplier row itself.
  const [owners, setOwners] = useState<ContentOwnerRecord[]>([]);

  const loadOwners = useCallback(() => {
    foyerApi.listContentOwners().then(setOwners, () => setOwners([]));
  }, []);

  useEffect(() => {
    loadOwners();
  }, [loadOwners]);

  const toggle = async (supplier: ContentSupplierRow) => {
    setBusy(supplier.id);
    setError(null);
    try {
      const res = await foyerApi.trustSupplier(supplier.id, !supplier.trusted);
      // Withdrawing trust silently empties every rule that named the supplier.
      // An operator who is not shown that finds out from a guest.
      setWarning(
        res.affectedPackages.length > 0
          ? `${supplier.name}: ${res.affectedPackages.length} package(s) changed — ${res.affectedPackages.map((p) => p.name).join(", ")}.`
          : null
      );
      await onChanged();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(deleting.id);
    setDeleteError(null);
    try {
      await foyerApi.deleteContentSupplier(deleting.id);
      setDeleting(null);
      await onChanged();
    } catch (e) {
      const err = e as { body?: { hint?: string }; message: string };
      setDeleteError(err.body?.hint ?? friendlyError(e));
    } finally {
      setBusy(null);
    }
  };

  const sync = async () => {
    setBusy("__sync__");
    setError(null);
    try {
      await foyerApi.syncContentSuppliers();
      await onChanged();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Content providers</Typography>
          <Typography variant="body2" color="text.secondary">
            A <strong>trusted</strong> content provider is one Nenda has a
            licence with. Their new titles enter every matching package
            automatically, with nobody looking at them. Everyone else can only
            enter a package by a pin, after a screening.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
            disabled={busy !== null}
            data-testid="content-supplier-add"
          >
            Add content provider
          </Button>
          <Button
            size="small"
            startIcon={<SyncIcon />}
            onClick={() => void sync()}
            disabled={busy !== null}
          >
            Sync from catalogue
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {warning ? (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      ) : null}

      {trustedCount === 0 ? (
        // The empty-estate case, and the one that matters most: with nothing
        // trusted, every rule anywhere matches nothing. Same lesson the ingest
        // picker learned the hard way (see ingest-entitlement-reachability).
        <Alert severity="warning" sx={{ mb: 2 }}>
          No content provider is trusted yet, so no rule can match anything.
          Every package is curated-only until a content provider is trusted
          here.
        </Alert>
      ) : null}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Content provider</TableCell>
              <TableCell>Id</TableCell>
              <TableCell>Login</TableCell>
              <TableCell>MRSS feed</TableCell>
              <TableCell>Trusted since</TableCell>
              <TableCell align="right">Trusted</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((supplier) => (
              <TableRow key={supplier.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{supplier.name}</Typography>
                    {!supplier.known ? (
                      <Tooltip title="In the catalogue, but nobody has ruled on it yet.">
                        <Chip size="small" label="Not ruled on" />
                      </Tooltip>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: "monospace", color: tokens.inkMuted }}
                  >
                    {supplier.id}
                  </Typography>
                </TableCell>
                {/* A login is a capability of the provider, not a binding to
                    set — its id IS this row's id (`[[mrss-feed-content-providers]]`).
                    Purely a status marker here: the icon appears only once a
                    login exists (click to edit it). It is created from the
                    Users page instead — see the module doc comment. */}
                <TableCell>
                  {supplier.hasLogin ? (
                    <Tooltip title="Has a login — edit contact, handle, revenue share.">
                      <IconButton
                        size="small"
                        disabled={busy !== null}
                        color="success"
                        onClick={() => {
                          const owner = owners.find(
                            (o) => o.id === supplier.id
                          );
                          if (owner) setEditingLogin(owner);
                        }}
                        aria-label={`Edit login for ${supplier.name}`}
                        data-testid={`supplier-edit-login-${supplier.id}`}
                      >
                        <LoginIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  {/* CONTENT-23: owners created before `kind` existed. Shown
                      so they can be found and filled in, not guessed. */}
                  {supplier.hasLogin &&
                  owners.find((o) => o.id === supplier.id)?.kind == null ? (
                    <Chip
                      size="small"
                      label="Kind not set"
                      data-testid={`supplier-kind-missing-${supplier.id}`}
                      sx={{ ml: 0.5 }}
                    />
                  ) : null}
                </TableCell>
                {/* Type C (`[[mrss-feed-content-providers]]`): a feed this
                    PROVIDER delivers through — deliberately independent of
                    Login (`hasLogin`). Unlike a login, a feed needs nobody to
                    use it, so — unlike Login — this table both shows AND
                    creates it: filled icon to edit, muted icon to add. */}
                <TableCell>
                  <Tooltip
                    title={
                      supplier.mrssFeedUrl
                        ? "Has an MRSS feed — click to edit its address."
                        : "No MRSS feed — click to add one."
                    }
                  >
                    <IconButton
                      size="small"
                      disabled={busy !== null}
                      // Has one: the SAME brand blue as every other primary
                      // affordance — a feed is a normal, positive state here,
                      // not a "success" to celebrate. Has none: a light grey,
                      // deliberately fainter than the row's own text, so it
                      // reads as absent rather than as another action.
                      color={supplier.mrssFeedUrl ? "primary" : undefined}
                      sx={
                        supplier.mrssFeedUrl
                          ? undefined
                          : { color: tokens.inkFaint }
                      }
                      onClick={() => setEditingFeed(supplier)}
                      aria-label={
                        supplier.mrssFeedUrl
                          ? `Edit MRSS feed for ${supplier.name}`
                          : `Add MRSS feed for ${supplier.name}`
                      }
                      data-testid={`supplier-mrss-feed-${supplier.id}`}
                    >
                      <RssFeedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {supplier.trustedAt
                      ? `${new Date(supplier.trustedAt).toLocaleDateString("sv-SE")}${supplier.trustedBy ? ` · ${supplier.trustedBy}` : ""}`
                      : "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Switch
                    checked={supplier.trusted}
                    disabled={busy !== null}
                    onChange={() => void toggle(supplier)}
                    inputProps={{
                      "aria-label": `Trust ${supplier.name}`,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip
                    title={
                      supplier.trusted
                        ? "Untrust before deleting — the two are separate steps."
                        : supplier.hasLogin
                          ? "A provider with a login cannot be deleted."
                          : "Delete"
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        color="error"
                        disabled={
                          busy !== null || supplier.trusted || supplier.hasLogin
                        }
                        onClick={() => {
                          setDeleteError(null);
                          setDeleting(supplier);
                        }}
                        aria-label={`Delete ${supplier.name}`}
                        data-testid={`supplier-delete-${supplier.id}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    No suppliers yet. “Sync from catalogue” lists everyone the
                    content catalogue knows about — it never grants trust.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      {adding ? (
        <AddSupplierDialog
          onClose={() => setAdding(false)}
          onCreated={async () => {
            setAdding(false);
            await onChanged();
          }}
        />
      ) : null}

      {editingLogin ? (
        <EditLoginDialog
          owner={editingLogin}
          onClose={() => setEditingLogin(null)}
          onSaved={async () => {
            setEditingLogin(null);
            loadOwners();
          }}
        />
      ) : null}

      {editingFeed ? (
        <EditFeedDialog
          supplier={editingFeed}
          onClose={() => setEditingFeed(null)}
          onSaved={async () => {
            setEditingFeed(null);
            await onChanged();
          }}
        />
      ) : null}

      <DangerConfirmDialog
        open={deleting !== null}
        title="Delete content provider"
        entityName={deleting?.name ?? ""}
        body="This removes the row from both Foyer and the content catalogue. It cannot be undone — a new one would need a new id."
        errorMessage={deleteError}
        busy={busy === deleting?.id}
        onConfirm={() => void remove()}
        onCancel={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
      />
    </Card>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Mint a real provider — never possible anywhere before this (`[[mrss-feed-content-providers]]`).
 * The legacy portal's own "add provider" tab writes a tag, not a row a
 * package rule can carry, which is exactly how Tigra ended up unusable.
 *
 * Created **untrusted** and with no login. Trust and login are both separate,
 * deliberate switches — minting the row is only the first act.
 */
function AddSupplierDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [providerId, setProviderId] = useState("");
  // Once the operator types into the id field directly, it stops following
  // the name — same rule as every other auto-derived field in this file.
  const [idEdited, setIdEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{
    field: string;
    hint: string;
  } | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    setFieldError(null);
    try {
      await foyerApi.createContentSupplier({
        name: name.trim(),
        // Undefined unless the operator deliberately typed one: the preview
        // is only a preview, and the server's own mintProviderId stays the
        // one canonical algorithm rather than two copies that could drift.
        providerId: idEdited ? providerId.trim() || undefined : undefined,
      });
      await onCreated();
    } catch (e) {
      const err = e as {
        code?: string;
        body?: { field?: string; hint?: string };
        message: string;
      };
      if (err.code === "already_taken" && err.body?.field) {
        setFieldError({
          field: err.body.field,
          hint: err.body.hint ?? "Already taken.",
        });
      } else if (err.code === "invalid" && err.body?.field) {
        setFieldError({
          field: err.body.field,
          hint: "That value is not one the server will accept.",
        });
      } else {
        setError(friendlyError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add content provider</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              const next = e.target.value;
              setName(next);
              if (!idEdited) setProviderId(slugPreview(next));
            }}
            size="small"
            required
            autoFocus
            error={fieldError?.field === "name"}
            helperText={
              fieldError?.field === "name"
                ? fieldError.hint
                : "As it should read on the Content providers list."
            }
            inputProps={{ "data-testid": "supplier-name" }}
          />

          <TextField
            label="Id"
            value={providerId}
            onChange={(e) => {
              setProviderId(e.target.value);
              setIdEdited(true);
            }}
            size="small"
            error={fieldError?.field === "providerId"}
            helperText={
              fieldError?.field === "providerId"
                ? fieldError.hint
                : "Filled in from the name — edit to override. Always minted as foyer-<id> — permanent the moment any title references it."
            }
            inputProps={{ "data-testid": "supplier-id" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={saving || name.trim() === ""}
          data-testid="supplier-save"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */

/** Basis points as a percentage, for a human reading the dialog. */
function sharePercent(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}

/**
 * Edit an existing login, in place on the provider row — replaces the
 * separate "content owners" card the first cut of this page had. See the
 * module doc comment for why that was wrong.
 *
 * The two fields NOT here, deliberately: `brand`/`identity` are the
 * supplier's OWN identity, owner-editable only at `PATCH /owner/branding` —
 * an operator quietly correcting a studio's copyright line is a divergence
 * the studio cannot see. There is no delete: `enabled: false` ends the
 * relationship reversibly, and it is also what keeps the underlying content
 * provider from ever being deletable again (see the delete button above).
 */
function EditLoginDialog({
  owner,
  onClose,
  onSaved,
}: {
  owner: ContentOwnerRecord;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(owner.name);
  const [slug, setSlug] = useState(owner.slug);
  const [contactEmail, setContactEmail] = useState(owner.contactEmail);
  const [enabled, setEnabled] = useState(owner.enabled);
  // `""` only for an owner created before `kind` existed; once set it can be
  // changed but not cleared, so the empty choice is not offered back.
  const [kind, setKind] = useState<ContentOwnerKindValue | "">(
    owner.kind ?? ""
  );
  const [share, setShare] = useState(
    owner.revenueShareBps === null ? "" : String(owner.revenueShareBps)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{
    field: string;
    hint: string;
  } | null>(null);

  const shareBps = share.trim() === "" ? null : Number(share.trim());
  const shareValid =
    shareBps === null ||
    (Number.isInteger(shareBps) && shareBps >= 0 && shareBps <= 10000);

  const save = async () => {
    setSaving(true);
    setError(null);
    setFieldError(null);
    try {
      await foyerApi.updateContentOwner(owner.id, {
        name: name.trim(),
        slug: slug.trim(),
        contactEmail: contactEmail.trim(),
        enabled,
        ...(kind ? { kind } : {}),
        revenueShareBps: shareBps,
      });
      await onSaved();
    } catch (e) {
      const err = e as {
        code?: string;
        body?: { field?: string; hint?: string };
        message: string;
      };
      if (err.code === "already_taken" && err.body?.field) {
        setFieldError({
          field: err.body.field,
          hint: err.body.hint ?? "Already taken.",
        });
      } else if (err.code === "invalid" && err.body?.field) {
        setFieldError({
          field: err.body.field,
          hint: "That value is not one the server will accept.",
        });
      } else {
        setError(friendlyError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit {owner.name}&apos;s login</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Organisation name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            required
            autoFocus
            error={fieldError?.field === "name"}
            helperText={
              fieldError?.field === "name"
                ? fieldError.hint
                : "The studio, creator or aggregator, as they are known."
            }
            inputProps={{ "data-testid": "edit-login-name" }}
          />

          <TextField
            label="Handle"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            size="small"
            error={fieldError?.field === "slug"}
            helperText={
              fieldError?.field === "slug"
                ? fieldError.hint
                : // 🚨 The storefront handle is live in every magic link
                  // already sent (`WL-7`). Changing it is not a rename.
                  "Their storefront is reached at /<handle>/login/… — changing it breaks every login link already sent."
            }
            inputProps={{ "data-testid": "edit-login-slug" }}
          />

          <TextField
            select
            label="Kind of organisation"
            value={kind}
            onChange={(e) => setKind(e.target.value as ContentOwnerKindValue)}
            size="small"
            error={fieldError?.field === "kind"}
            helperText={
              kind === ""
                ? "Not set yet - this owner predates it. Pick one."
                : "Used to filter the review queue, and for terms that differ per type."
            }
            inputProps={{ "data-testid": "edit-login-kind" }}
          >
            {CONTENT_OWNER_KIND_OPTIONS.map((k) => (
              <MenuItem key={k.value} value={k.value}>
                {k.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Contact email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            size="small"
            helperText="Who to reach about their material. This is NOT a login — that is a user on the Users page."
            inputProps={{ "data-testid": "edit-login-contact" }}
          />

          <TextField
            label="Revenue share (basis points)"
            value={share}
            onChange={(e) => setShare(e.target.value)}
            size="small"
            error={!shareValid || fieldError?.field === "revenueShareBps"}
            helperText={
              !shareValid
                ? "A whole number of basis points, 0 to 10000."
                : fieldError?.field === "revenueShareBps"
                  ? fieldError.hint
                  : shareBps === null
                    ? "Empty means NOT AGREED, which is not the same as zero. Both are decisions worth making explicitly."
                    : `${sharePercent(shareBps)} to them. 10000 is the whole of it.`
            }
            inputProps={{ "data-testid": "edit-login-share" }}
          />

          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              inputProps={{ "aria-label": "Enabled" } as never}
              data-testid="edit-login-enabled"
            />
            <Typography variant="body2">
              {enabled
                ? "Enabled — their login works."
                : "Disabled — their login is refused at the shell. Nothing they hold has been touched."}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={saving || name.trim() === "" || !shareValid}
          data-testid="edit-login-save"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Attach, change or clear a provider's MRSS feed — Type C in the A/B/C shape
 * (`[[mrss-feed-content-providers]]`).
 *
 * Deliberately its OWN dialog, not a field on `EditLoginDialog` — ruled the
 * same day the field first landed on the login and moved: a feed-only
 * aggregator with nobody at the source ever logging in still needs one, and
 * putting it behind "edit login" would put it behind a login that provider
 * will never have. This dialog opens for ANY provider row, `hasLogin` or not.
 */
function EditFeedDialog({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: ContentSupplierRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [mrssFeedUrl, setMrssFeedUrl] = useState(supplier.mrssFeedUrl ?? "");
  const [effectChannelId, setEffectChannelId] = useState(
    supplier.effectChannelId ?? ""
  );
  const [channels, setChannels] = useState<SignagePlaylist[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [channelError, setChannelError] = useState<string | null>(null);
  const clearing = mrssFeedUrl.trim() === "";

  const mrssFeedUrlValid = looksLikeMrssFeedUrl(mrssFeedUrl);
  // Reported by Sigge from production: a feed URL was saved with no channel
  // ever chosen, and every scan silently read the whole feed as "new"
  // forever. The server now refuses to save that combination outright - this
  // mirrors the same rule here so the operator sees it before clicking Save,
  // not after.
  const channelMissing = !clearing && !effectChannelId;

  useEffect(() => {
    let alive = true;
    foyerApi
      .listSignagePlaylistsIn({ kind: "global" })
      .then((rows) => {
        if (!alive) return;
        setChannels(rows.filter((p) => p.scope === "global" && p.effect));
      })
      .catch(() => alive && setChannels([]));
    return () => {
      alive = false;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setFieldError(null);
    setChannelError(null);
    try {
      // Both fields in ONE call, atomically - never a URL saved with the
      // channel choice as a second request that could be skipped. That
      // sequential pattern is exactly how the production row went wrong.
      await foyerApi.setSupplierMrssFeed(
        supplier.id,
        clearing ? null : mrssFeedUrl.trim(),
        effectChannelId || null
      );
      await onSaved();
    } catch (e) {
      const err = e as {
        code?: string;
        body?: { field?: string; hint?: string };
        message: string;
      };
      if (err.code === "effect_channel_required") {
        setChannelError(
          err.body?.hint ?? "Choose the channel this feed's items join."
        );
      } else if (
        err.code === "invalid" &&
        err.body?.field === "effectChannelId"
      ) {
        setChannelError(err.body.hint ?? "That is not a valid effect channel.");
      } else if (err.code === "invalid" && err.body?.field === "mrssFeedUrl") {
        // Every other refusal on this route names the URL field - the check
        // that fetches the feed and confirms it looks like RSS/MRSS, not
        // just that it parses as a URL (`[[mrss-feed-content-providers]]`).
        setFieldError(err.body.hint ?? "That feed could not be verified.");
      } else {
        setError(friendlyError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{supplier.name}&apos;s MRSS feed</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Alert severity="info">
            Saving fetches the feed to confirm it answers and looks like
            RSS/MRSS. Scan it from the review queue&apos;s Feed tab to see what
            it carries and turn an item into a real title.
          </Alert>

          <TextField
            label="MRSS feed URL"
            value={mrssFeedUrl}
            onChange={(e) => {
              setMrssFeedUrl(e.target.value);
              setFieldError(null);
            }}
            size="small"
            autoFocus
            error={!mrssFeedUrlValid || !!fieldError}
            helperText={
              fieldError
                ? fieldError
                : !mrssFeedUrlValid
                  ? "That doesn't look like a URL."
                  : "Leave empty to remove the feed — this provider still exists either way."
            }
            inputProps={{ "data-testid": "edit-feed-url" }}
          />

          <TextField
            select
            label="Effect channel"
            value={effectChannelId}
            onChange={(e) => {
              setEffectChannelId(e.target.value);
              setChannelError(null);
            }}
            size="small"
            disabled={!channels}
            required={!clearing}
            error={!!channelError || channelMissing}
            helperText={
              channelError
                ? channelError
                : clearing
                  ? "No feed, so nothing to map — removed with the feed."
                  : channelMissing
                    ? "Required — an item approved from this feed needs a channel to join."
                    : "An item approved from this feed joins this channel automatically."
            }
            inputProps={{ "data-testid": "edit-feed-effect-channel" }}
          >
            {(channels ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={saving || !mrssFeedUrlValid || channelMissing}
          data-testid="edit-feed-save"
        >
          {saving ? (clearing ? "Saving…" : "Verifying…") : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
